
import { useState, useEffect } from 'react';
import { Search, BarChart4, Settings, Menu, X, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import LakshBotLogo from './LakshBotLogo';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';

interface HeaderProps {
  onToggleMenu: () => void;
  menuOpen: boolean;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
}

const Header = ({ onToggleMenu, menuOpen, activeTab = "dashboard", onTabChange }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const { user, signOut } = useAuth();
  const initials = (user?.user_metadata?.display_name || user?.email || "U")
    .split(/[\s@]/)[0]
    .slice(0, 2)
    .toUpperCase();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (tabId: string) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between transition-all duration-300",
        scrolled ? "bg-background/80 backdrop-blur-md border-b" : "bg-transparent"
      )}
    >
      <div className="flex items-center space-x-6">
        <button 
          onClick={onToggleMenu}
          className="p-2 rounded-full hover:bg-muted transition-colors lg:hidden"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        
        <div className="flex items-center">
          <LakshBotLogo size={28} />
        </div>
        
        <nav className="hidden lg:flex items-center space-x-6">
          <button 
            onClick={() => handleNavClick('dashboard')}
            className={cn(
              "text-sm font-medium transition-colors", 
              activeTab === 'dashboard' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Dashboard
          </button>
          <button 
            onClick={() => handleNavClick('analysis')}
            className={cn(
              "text-sm font-medium transition-colors", 
              activeTab === 'analysis' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Analysis
          </button>
          <button 
            onClick={() => handleNavClick('prediction')}
            className={cn(
              "text-sm font-medium transition-colors", 
              activeTab === 'prediction' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Predictions
          </button>
          <button 
            onClick={() => handleNavClick('trading')}
            className={cn(
              "text-sm font-medium transition-colors", 
              activeTab === 'trading' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Trading
          </button>
        </nav>
      </div>
      
      <div className="flex items-center space-x-4">
        <div 
          className={cn(
            "relative transition-all duration-300 ease-in-out",
            searchFocused ? "w-80" : "w-40 lg:w-60"
          )}
        >
          <div className={cn(
            "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none",
            searchFocused ? "text-primary" : "text-muted-foreground"
          )}>
            <Search size={16} />
          </div>
          <input
            type="text"
            className={cn(
              "py-2 pl-10 pr-4 w-full rounded-full bg-muted/50 border-transparent focus:border-primary focus:ring-0 focus:outline-none transition-all",
              searchFocused ? "bg-background border-muted" : "hover:bg-muted"
            )}
            placeholder="Search stocks, indices..."
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </div>
        
        <button
          className="p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="View charts"
        >
          <BarChart4 size={20} className="text-muted-foreground hover:text-foreground transition-colors" />
        </button>
        
        <button
          className="p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="Settings"
        >
          <Settings size={20} className="text-muted-foreground hover:text-foreground transition-colors" />
        </button>
        
        {user ? (
          <>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center" title={user.email ?? undefined}>
              <span className="text-xs font-medium">{initials}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={signOut} aria-label="Sign out">
              <LogOut size={18} />
            </Button>
          </>
        ) : (
          <Link to="/auth">
            <Button size="sm">Sign In</Button>
          </Link>
        )}
      </div>
    </header>
  );
};

export default Header;
